import React, { useEffect, useRef, useState } from "react";
import Chip from "@material-ui/core/Chip";
import Tooltip from "@material-ui/core/Tooltip";

export const STATUS_PAGE_URL = "https://stats.uptimerobot.com/pCLCX7vvJ2";
const STATUS_API_URL =
  "https://stats.uptimerobot.com/api/getMonitorList/pCLCX7vvJ2";
// The endpoint is cached server-side with max-age=60 and rate-limited to
// 60 requests/minute, so polling faster than once a minute buys nothing.
const POLL_INTERVAL_MS = 60000;
const MAX_CONSECUTIVE_FAILURES = 3;

const UNKNOWN_STATUS = {
  key: "unknown",
  label: "Status unknown",
  upCount: null,
  totalCount: null
};

export const deriveSystemStatus = payload => {
  const counts =
    payload && payload.statistics ? payload.statistics.counts : null;
  const up = Number(counts?.up);
  const down = Number(counts?.down);
  const total = Number(counts?.total);

  if (
    !Number.isFinite(up) ||
    !Number.isFinite(down) ||
    !Number.isFinite(total) ||
    total <= 0
  ) {
    return UNKNOWN_STATUS;
  }

  if (down === 0) {
    return {
      key: "operational",
      label: "Systems operational",
      upCount: up,
      totalCount: total
    };
  }

  if (down < total) {
    return {
      key: "degraded",
      label: "Partial outage",
      upCount: up,
      totalCount: total
    };
  }

  return { key: "outage", label: "Major outage", upCount: up, totalCount: total };
};

const SystemStatusChip = () => {
  const [status, setStatus] = useState(null);
  const failureCountRef = useRef(0);

  useEffect(() => {
    let isMounted = true;

    // Deliberately window.fetch rather than axios: the app stamps auth headers
    // onto global axios defaults (actions/Auth.js, components/UserInfo) and
    // util/Api attaches flipapi tokens — none of that may leak to the
    // third-party status host.
    const fetchStatus = async () => {
      const controller = new AbortController();
      const timeoutId = window.setTimeout(() => controller.abort(), 15000);
      try {
        const response = await window.fetch(STATUS_API_URL, {
          signal: controller.signal
        });
        if (!response.ok) {
          throw new Error(`Status endpoint returned ${response.status}`);
        }
        const payload = await response.json();
        if (!isMounted) {
          return;
        }
        failureCountRef.current = 0;
        setStatus(deriveSystemStatus(payload));
      } catch (error) {
        if (!isMounted) {
          return;
        }
        // Keep showing the last known status through transient failures, but
        // stop claiming it once the endpoint has been unreachable for a while.
        failureCountRef.current += 1;
        if (failureCountRef.current >= MAX_CONSECUTIVE_FAILURES) {
          setStatus(UNKNOWN_STATUS);
        }
      } finally {
        window.clearTimeout(timeoutId);
      }
    };

    fetchStatus();
    const intervalId = window.setInterval(() => {
      if (document.hidden) {
        return;
      }
      fetchStatus();
    }, POLL_INTERVAL_MS);

    return () => {
      isMounted = false;
      window.clearInterval(intervalId);
    };
  }, []);

  if (!status) {
    return null;
  }

  const tooltip =
    status.totalCount != null
      ? `${status.upCount} of ${status.totalCount} monitors up — open status page`
      : "Open status page";

  return (
    <Tooltip title={tooltip}>
      <Chip
        size="small"
        clickable
        component="a"
        href={STATUS_PAGE_URL}
        target="_blank"
        rel="noopener noreferrer"
        label={status.label}
        className={`system-status-chip system-status-chip--${status.key}`}
      />
    </Tooltip>
  );
};

export default SystemStatusChip;
